import { useState } from "react";
import axios from "axios";
import { Phone, Printer, MapPin, Mail, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { CONTACT } from "../constants/testIds";
import { COMPANY, PROJECT_TYPES } from "../lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  project_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "" });
    try {
      const payload = {
        ...form,
        phone: form.phone || null,
        company: form.company || null,
        project_type: form.project_type || null,
      };
      const res = await axios.post(`${API}/contact`, payload);
      if (res.status === 201 || res.status === 200) {
        setStatus({
          state: "success",
          message: "Thanks! Your request has been received. Our team will reach out shortly.",
        });
        setForm(EMPTY);
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us directly.";
      setStatus({ state: "error", message: String(msg) });
    }
  };

  return (
    <div data-testid={CONTACT.root}>
      <section className="bg-[color:var(--brand-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-20" />
        <div className="container-tg relative py-24 lg:py-32">
          <div className="overline text-[color:var(--brand-yellow)] mb-6">Contact</div>
          <h1 className="font-display text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none max-w-4xl">
            Tell us about your <span className="text-[color:var(--brand-yellow)]">build</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Share your project — boat model, scope, references. We'll respond
            with a tailored plan and a quote.
          </p>
        </div>
      </section>

      <section className="container-tg py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4 space-y-10">
          <div>
            <div className="overline mb-3">Visit</div>
            <p className="text-slate-700 leading-relaxed">
              {COMPANY.address.street}
              <br />
              {COMPANY.address.city}
              <br />
              {COMPANY.address.country}
            </p>
          </div>
          <div className="space-y-4">
            <div className="overline mb-1">Reach</div>
            <a
              href={`tel:${COMPANY.phone}`}
              className="flex items-center gap-3 text-slate-700 hover:text-[color:var(--brand-navy)]"
            >
              <span className="w-9 h-9 bg-[color:var(--brand-navy)] text-[color:var(--brand-yellow)] flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </span>
              <span className="font-semibold">{COMPANY.phone}</span>
            </a>
            <div className="flex items-center gap-3 text-slate-700">
              <span className="w-9 h-9 bg-white border border-[color:var(--surface-border)] text-[color:var(--brand-navy)] flex items-center justify-center">
                <Printer className="w-4 h-4" />
              </span>
              <span>Fax: {COMPANY.fax}</span>
            </div>
            <a
              href={`mailto:${COMPANY.email}`}
              className="flex items-center gap-3 text-slate-700 hover:text-[color:var(--brand-navy)]"
            >
              <span className="w-9 h-9 bg-[color:var(--brand-yellow)] text-[color:var(--brand-navy)] flex items-center justify-center">
                <Mail className="w-4 h-4" />
              </span>
              <span className="font-semibold">{COMPANY.email}</span>
            </a>
          </div>
          <div className="card-tg">
            <div className="overline mb-3">Hours</div>
            <ul className="text-sm text-slate-600 space-y-1.5">
              <li className="flex justify-between"><span>Mon – Fri</span><span>8:00 — 17:00</span></li>
              <li className="flex justify-between"><span>Saturday</span><span>By appointment</span></li>
              <li className="flex justify-between"><span>Sunday</span><span>Closed</span></li>
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <form
            data-testid={CONTACT.form}
            onSubmit={onSubmit}
            className="bg-white border border-[color:var(--surface-border)] p-8 lg:p-12 space-y-6"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Name *" id="name">
                <input
                  data-testid={CONTACT.name}
                  required
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  className={inputCls}
                  placeholder="John Doe"
                />
              </Field>
              <Field label="Email *" id="email">
                <input
                  data-testid={CONTACT.email}
                  required
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className={inputCls}
                  placeholder="you@yacht.com"
                />
              </Field>
              <Field label="Phone" id="phone">
                <input
                  data-testid={CONTACT.phone}
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  className={inputCls}
                  placeholder="+1 (555) 555-5555"
                />
              </Field>
              <Field label="Company / Boat Make" id="company">
                <input
                  data-testid={CONTACT.company}
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={onChange}
                  className={inputCls}
                  placeholder="Sea Ray, Boston Whaler…"
                />
              </Field>
            </div>

            <Field label="Project Type" id="project_type">
              <select
                data-testid={CONTACT.projectType}
                name="project_type"
                value={form.project_type}
                onChange={onChange}
                className={`${inputCls} appearance-none`}
              >
                <option value="">Select a project type…</option>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Project Details *" id="message">
              <textarea
                data-testid={CONTACT.message}
                required
                name="message"
                rows={6}
                value={form.message}
                onChange={onChange}
                className={`${inputCls} resize-y`}
                placeholder="Tell us about your project — boat year/model, what you'd like done, finish preferences…"
              />
            </Field>

            {status.state === "success" && (
              <div
                data-testid={CONTACT.success}
                className="flex items-start gap-3 bg-[color:var(--brand-yellow)]/20 border border-[color:var(--brand-yellow)] p-4"
              >
                <CheckCircle2 className="w-5 h-5 text-[color:var(--brand-navy)] shrink-0 mt-0.5" />
                <p className="text-sm text-[color:var(--brand-navy)] font-medium">{status.message}</p>
              </div>
            )}
            {status.state === "error" && (
              <div
                data-testid={CONTACT.error}
                className="flex items-start gap-3 bg-red-50 border border-red-200 p-4"
              >
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{status.message}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
              <p className="text-xs text-slate-500">
                By submitting this form you agree to be contacted by Tecnografic.
              </p>
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={status.state === "submitting"}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.state === "submitting" ? "Sending…" : "Send Inquiry"}
                {status.state !== "submitting" && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[color:var(--surface-bg)] border border-[color:var(--surface-border)] px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[color:var(--brand-navy)] focus:ring-2 focus:ring-[color:var(--brand-yellow)] rounded-none transition";

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="block text-xs font-bold uppercase tracking-widest text-[color:var(--brand-navy)] mb-2">
      {label}
    </span>
    {children}
  </label>
);
